"use client";
import styles from "../css/components/stats-card.module.css";
import {
  FiUsers,
  FiMonitor,
  FiThermometer,
  FiAlertTriangle,
  FiClock,
} from "react-icons/fi";
import {
  Edit3,
  ShoppingBag,
  CalendarClock,
  Stethoscope,
  Lock,
  Printer,
  Truck,
  Hospital,
  BarChart3,
  Repeat,
  Package,
  Info,
  FileText,
  DollarSign,
  Banknote,
  Coins,
  Wallet,
  CreditCard,
  Pill,
  Hourglass,
  CalendarCheck2,
  Boxes,
  UserPlus,
  Edit, // Added for "Update Profile"
  GitMerge, // Added for "Merge Profiles"
  Split, // Added for "Split Profile"
  UserCog, // Added for "Add Doctor" (lucide-react equivalent for user-md)
} from "lucide-react";
import { motion } from "framer-motion";

export function StatsCard({
  title,
  value,
  icon,
  children,
  variant = "default",
  onClick,
  inlineIcon = false,
  tall = false,
  className = "",
  style = {},
  titleSize,
  valueSize,
  interactive = true,
}) {
  // Simple letter glyph component to use as an icon
  const RandGlyph = () => (
    <span className={styles.letterIcon} aria-hidden>
      R
    </span>
  );

  const iconMap = {
    users: FiUsers,
    monitor: FiMonitor,
    thermometer: FiThermometer,
    alert: FiAlertTriangle,
    expiry: FiClock,
    "file-text": FileText,
    note: FileText,
    "dollar-sign": DollarSign,
    banknote: Banknote,
    "money-note": Banknote,
    cash: Banknote,
    coins: Coins,
    wallet: Wallet,
    "credit-card": CreditCard,
    "edit-3": Edit3,
    "shopping-bag": ShoppingBag,
    "calendar-clock": CalendarClock,
    stethoscope: Stethoscope,
    lock: Lock,
    printer: Printer,
    truck: Truck,
    hospital: Hospital,
    "bar-chart-3": BarChart3,
    repeat: Repeat,
    package: Package,
    info: Info,
    pill: Pill,
    hourglass: Hourglass,
    "calendar-check-2": CalendarCheck2,
    boxes: Boxes,
    "user-plus": UserPlus,
    edit: Edit,
    "git-merge": GitMerge,
    split: Split,
    "user-md": UserCog,
    // Custom letter glyphs
    r: RandGlyph,
    rand: RandGlyph,
  };

  const normalizedIcon = icon ? icon.toLowerCase() : null;
  const IconComponent = iconMap[normalizedIcon] || FiMonitor;
  if (icon && !iconMap[normalizedIcon]) {
    console.warn(`StatsCard: Unknown icon "${icon}", defaulting to FiMonitor`);
  }

  const cardClass = `${styles.card} ${tall ? styles.tall : ""} ${
    variant === "square"
      ? styles.square
      : variant === "vertical"
      ? styles.vertical
      : variant === "form"
      ? styles.formCard
      : ""
  }`;

  const combinedClass = `${cardClass} ${className} ${!interactive ? styles.nonInteractive : ""}`.trim();

  if (variant === "form") {
    return (
      <div className={combinedClass} style={style}>
        {icon && (
          <div className={styles.icon}>
            <IconComponent size={24} />
          </div>
        )}
        <div className={styles.content}>
          <p className={styles.title} style={titleSize ? { fontSize: titleSize } : undefined}>{title}</p>
          {value !== undefined && value !== null && (
            <p className={styles.value} style={valueSize ? { fontSize: valueSize } : undefined}>{value}</p>
          )}
          {children && <div className={styles.customContent}>{children}</div>}
        </div>
      </div>
    );
  }
  return (
    <motion.div
      className={combinedClass}
      style={style}
      onClick={interactive ? onClick : undefined}
      whileHover={interactive ? { scale: 1.05 } : undefined}
      whileTap={interactive ? { scale: 0.93 } : undefined}
    >
      <div className={styles.content}>
        {variant === "square" || variant === "vertical" ? (
          <>
            {icon && (
              <div className={styles.icon} aria-label={`${title} icon`}>
                <IconComponent size={32} />
              </div>
            )}
            <div className={styles.info}>
              <p className={styles.title} style={titleSize ? { fontSize: titleSize } : undefined}>{title}</p>
              {value !== undefined && value !== null && (
                <p className={styles.value} style={valueSize ? { fontSize: valueSize } : undefined}>{value}</p>
              )}
              {children && (
                <div className={styles.customContent}>{children}</div>
              )}
            </div>
          </>
        ) : (
          <>
            {inlineIcon ? (
              <div className={styles.info}>
                <div className={styles.titleRow}>
                  {icon && (
                    <span className={styles.iconInline} aria-label={`${title} icon`}>
                      <IconComponent size={18} />
                    </span>
                  )}
                  <p className={styles.title} style={titleSize ? { fontSize: titleSize } : undefined}>{title}</p>
                </div>
                {value !== undefined && value !== null && (
                  <p className={styles.value} style={valueSize ? { fontSize: valueSize } : undefined}>{value}</p>
                )}
                {children && (
                  <div className={styles.customContent}>{children}</div>
                )}
              </div>
            ) : (
              <>
                <div className={styles.info}>
                  <p className={styles.title} style={titleSize ? { fontSize: titleSize } : undefined}>{title}</p>
                  {value !== undefined && value !== null && (
                    <p className={styles.value} style={valueSize ? { fontSize: valueSize } : undefined}>{value}</p>
                  )}
                  {children && (
                    <div className={styles.customContent}>{children}</div>
                  )}
                </div>
                {icon && (
                  <div className={styles.icon} aria-label={`${title} icon`}>
                    <IconComponent size={24} />
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </motion.div>
  );
}

export default StatsCard;